import { LinkWithSlug } from "routes";

import { Button } from "@/components/elements/Button";
import Tooltip from "@/components/elements/Tooltip";
import { useViewDataSourceSlideOver } from "@/components/modules/view-data-source-slideover";
import { formatModelName } from "@/features/models";
import { IntegrationLogoBox } from "@/integrations";
import { ModelIcon } from "@/pages/ModelTool/components/ModelIcon/ModelIcon";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { truncateStringMiddle } from "@/shared/formatters/truncation";

import { RowData } from "../columns";

export const ModelSourceCell = ({ model }: Pick<RowData, "model">) => {
  if (!model) return <div />;
  const modelName = formatModelName(model);
  const truncatedModelPath = truncateStringMiddle(modelName, 30);
  return (
    <div className="-mx-2 whitespace-nowrap">
      <Tooltip content={modelName}>
        <Button
          variant="ghost"
          icon={<ModelIcon model={model} />}
          size="sm"
          className="h-7 min-w-0 max-w-52 truncate px-1.5 text-xs font-normal leading-none hover:border-gray-300 dark:hover:border-gray-600 sm:text-sm"
          as={LinkWithSlug}
          to={`/editor/${model.id}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <span className="truncate">{truncatedModelPath}</span>
        </Button>
      </Tooltip>
    </div>
  );
};

export const RawViewSourceCell = ({
  rawView,
}: {
  rawView: NonNullable<RowData["rawView"]>;
}) => {
  const dwh = useDataWarehouseContext();
  const { onOpen: viewSync } = useViewDataSourceSlideOver();

  const tooltip = `${rawView.path.join(".")} (${rawView.integrationId})`;

  if (!rawView.syncId) {
    return (
      <div className="-mx-[2px] whitespace-nowrap">
        <Tooltip content={`Not referenced by any sync - ${tooltip}`}>
          <span className="inline-flex min-w-0 items-center text-sm">
            <IntegrationLogoBox
              id={dwh.integration.id}
              className="mr-1 h-5 w-5"
            />
            <span className="truncate">
              {rawView.schema}.{rawView.sourceStream}
            </span>
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="-mx-2 whitespace-nowrap">
      <Tooltip content={tooltip}>
        <span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 min-w-0 max-w-52 truncate px-1.5 text-xs font-normal leading-none hover:border-gray-300 dark:hover:border-gray-600 sm:text-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (!rawView.syncId) {
                return;
              }
              viewSync({ syncId: rawView.syncId });
            }}
          >
            <div className="mr-1 flex items-center gap-0.5">
              <IntegrationLogoBox id={dwh.integration.id} className="h-5 w-5" />
              {rawView.integrationId && (
                <IntegrationLogoBox
                  id={rawView.integrationId}
                  className="h-5 w-5"
                />
              )}
            </div>
            <span className="truncate">
              {rawView.schema}.{rawView.sourceStream}
            </span>
          </Button>
        </span>
      </Tooltip>
    </div>
  );
};
